import { WithdrawalForm as WithdrawalFormDto } from "@/api/dtos";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { ConditionMode, ValidateOn } from "@ngt/forms-core";
import { CollaboratingGroupContext, CrfCondition, CrfForm, DatePicker, FormBreadcrumbs, GroupedField, ICrfConditionParameters, ICrfConditionProps, IGroupedFieldProps, InstitutionContext, MasterGroupContext, PageLayout, PatientContext, RadioGroup, RouteLoading } from "@ngt/opms";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import { DateTime } from "luxon";
import { FC, useCallback, useContext } from "react";

interface IWithdrawalFormProps {
    
}

interface IConditionedRadioGroupFieldProps<TValues extends object = any> {
    ConditionProps: ICrfConditionProps<TValues>,
    GroupedFieldProps: Omit<IGroupedFieldProps, 'component'>
}

const ConditionedRadioGroupField: FC<IConditionedRadioGroupFieldProps> = ({
    ConditionProps,
    GroupedFieldProps
}) => {

    return (
        <CrfCondition
            mode={ConditionMode.Enable}
            {...ConditionProps}
        >
            <GroupedField
                component={RadioGroup}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
                LabelGridProps={{
                    size: {
                        xl: 6,
                        lg: 6,
                        md: 6,
                        sm: 6,
                        xs: 12
                    }
                }}
                ContainerGridProps={{
                    sx: {
                        alignItems: "flex-start !important",
                        justifyContent: "space-between"
                    },
                }}
                {...GroupedFieldProps}
            />
        </CrfCondition>
    )
}

const WithdrawalForm: FC<IWithdrawalFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    
    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );
    
    const dateOfConsentWithdrawalNotEmpty = useCallback((params: ICrfConditionParameters<WithdrawalFormDto>) => {
        return !!params.formState?.value?.consentWithdrawalDate && DateTime.fromISO(params.formState?.value?.consentWithdrawalDate).isValid
    }, []);


    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }
    
    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm 
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
            >
                <GroupedField
                    propertyPath="consentWithdrawalDate"
                    component={DatePicker}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                    LabelGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                    ContainerGridProps={{
                        sx: {
                            justifyContent: "space-between"
                        },
                    }}
                />
                <ConditionedRadioGroupField
                    ConditionProps={{
                        condition: dateOfConsentWithdrawalNotEmpty
                    }}
                    GroupedFieldProps={{
                        propertyPath: "contact"
                    }}
                />
                <ConditionedRadioGroupField
                    ConditionProps={{
                        condition: dateOfConsentWithdrawalNotEmpty
                    }}
                    GroupedFieldProps={{
                        propertyPath: "healthInformation"
                    }}
                />
                <ConditionedRadioGroupField
                    ConditionProps={{
                        condition: dateOfConsentWithdrawalNotEmpty
                    }}
                    GroupedFieldProps={{
                        propertyPath: "sample"
                    }}
                />
                <ConditionedRadioGroupField
                    ConditionProps={{
                        condition: dateOfConsentWithdrawalNotEmpty
                    }}
                    GroupedFieldProps={{
                        propertyPath: "sampleFuture"
                    }}
                />
            </CrfForm>
        </PageLayout>
    )
}

export default WithdrawalForm;